import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Search, Filter, Edit, Trash2, Eye, X, RefreshCw, Archive, ArchiveRestore, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import CancelTripDialog from '@/components/credits/CancelTripDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Phase 17.2 #4 — a quote is "expired" when its expiryDate is strictly in the
// past AND it hasn't been won/closed yet. Confirmed / completed / cancelled
// quotes are terminal, so the expiry flag is irrelevant for them.
const isQuoteExpired = (quote) => {
  if (!quote || !quote.expiryDate) return false;
  const terminal = ['confirmed', 'completed', 'cancelled'];
  if (terminal.includes((quote.status || '').toLowerCase())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(quote.expiryDate);
  return !isNaN(exp.getTime()) && exp < today;
};

// Session F follow-up — a multi-option quote (Directive 2) with pricingOptions
// but no option marked isSelected would 400 the moment an agent tries to
// invoice it (see routes/invoices.py create_invoice). Surface that BEFORE
// they hit the error, not after.
const needsOptionSelection = (quote) => {
  const options = quote?.pricingOptions || [];
  return options.length > 0 && !options.some((o) => o.isSelected);
};

const QuotesList = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pre-fill from ?search= when Analytics deep-links here for drill-down.
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  // Pre-fill the status filter from ?status= when the dashboard links here.
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  // Phase 12 — quote type filter (custom / flight / hotel / attraction / visa).
  const [quoteTypeFilter, setQuoteTypeFilter] = useState(searchParams.get('quoteType') || 'all');
  // True when navigated here from Analytics drill-down; gates the clear-filter banner.
  const fromAnalytics = searchParams.get('from') === 'analytics';
  // True when navigated here from the Collections widget's overdue alert.
  // Overdue is a derived state (dueDate < today AND outstanding > 0), so the
  // filtering is done client-side after the trip list arrives.
  const fromCollections = searchParams.get('from') === 'collections';
  const overdueOnly = searchParams.get('status') === 'overdue' && fromCollections;
  const { user } = useAuth();
  const canWrite = (user?.role || '').toLowerCase() !== 'accounts';
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  // Data-integrity cleanup (2026-10-06) — "Show archived" reveals soft-deleted
  // trips (orphans left behind by the itinerary-duplication bug) so an admin
  // can Restore a wrongly-archived one or leave it hidden for good.
  const [showDeleted, setShowDeleted] = useState(false);
  const canDecideCancellation = ['admin', 'accounts'].includes((user?.role || '').toLowerCase());
  const [pendingCancellations, setPendingCancellations] = useState([]);
  const [cancelDialogTripId, setCancelDialogTripId] = useState(null);

  const fetchPendingCancellations = async () => {
    if (!canDecideCancellation) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/cancellations/pending`);
      setPendingCancellations(r.data || []);
    } catch (err) {
      // non-critical — silently skip, worklist just won't show
    }
  };

  useEffect(() => {
    fetchPendingCancellations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, quoteTypeFilter, searchTerm, overdueOnly, showDeleted]);

  const fetchQuotes = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all' && !overdueOnly) params.append('status', statusFilter);
      if (quoteTypeFilter !== 'all') params.append('quoteType', quoteTypeFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (showDeleted) params.append('include_deleted', 'true');

      const response = await axios.get(`${BACKEND_URL}/api/quotes?${params.toString()}`);
      let rows = showDeleted ? response.data.filter((t) => t.deletedAt) : response.data;
      // Overdue filter — keep trips where outstanding > 0 AND dueDate < today.
      // We do this client-side because "overdue" isn't a stored status; it's
      // derived from receipts + due-date math.
      if (overdueOnly) {
        const today = new Date().toISOString().slice(0, 10);
        rows = rows.filter((t) => {
          const total = Number(t.totalCost || 0);
          const paid = (t.receipts || []).reduce((s, r) => s + Number(r.amount || 0), 0);
          const outstanding = total - paid;
          const due = (t.dueDate || '').slice(0, 10);
          return outstanding > 0 && due && due < today;
        });
      }
      setQuotes(rows);
    } catch (error) {
      toast.error('Failed to fetch quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/quotes/${id}`);
      toast.success('Quote deleted successfully');
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm('Archive this quote? It disappears from the list but every record stays intact — you can restore it any time.')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/quotes/${id}/soft-delete`);
      toast.success('Quote archived');
      fetchQuotes();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to archive quote');
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${BACKEND_URL}/api/quotes/${id}/restore`);
      toast.success('Quote restored');
      fetchQuotes();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to restore quote');
    }
  };

  // Session F follow-up — one-click Renew on an expired quote. Extends
  // expiryDate forward from today (backend default: company_settings
  // quoteExpiryDays, or 7) and logs who/when server-side.
  const [renewingId, setRenewingId] = useState(null);
  const handleRenew = async (quote) => {
    setRenewingId(quote.id);
    try {
      await axios.post(`${BACKEND_URL}/api/trips/${quote.id}/renew`);
      toast.success('Quote renewed — new expiry date set');
      fetchQuotes();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to renew quote');
    } finally {
      setRenewingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-500">Loading quotes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 tracking-tight">
            Quotes
          </h1>
          <p className="text-base text-slate-600 mt-2">Manage all your travel quotes</p>
        </div>
        
        {canWrite && (
        <Link to="/quotes/new">
          <Button 
            data-testid="create-new-quote-btn"
            className="bg-teal-700 text-white hover:bg-teal-800 shadow-sm font-medium px-6 py-2.5 rounded-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Quote
          </Button>
        </Link>
        )}
      </div>

      {isAdmin && (
        <div className="flex items-center justify-end">
          <button
            type="button"
            data-testid="toggle-archived-quotes-btn"
            onClick={() => setShowDeleted((v) => !v)}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
              showDeleted ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <Archive className="h-3.5 w-3.5" />
            {showDeleted ? 'Showing archived' : 'Show archived'}
          </button>
        </div>
      )}

      {canDecideCancellation && pendingCancellations.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="pending-cancellations-panel">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h2 className="text-sm font-semibold text-amber-900">
              {pendingCancellations.length} trip(s) need a cancellation money decision
            </h2>
          </div>
          <div className="space-y-2">
            {pendingCancellations.map((p) => (
              <div
                key={p.tripId}
                className="flex items-center justify-between gap-3 bg-white rounded-lg border border-amber-100 px-3 py-2"
                data-testid={`pending-cancellation-row-${p.tripId}`}
              >
                <div className="text-sm">
                  <span className="font-medium text-slate-800">{p.displayTripId}</span>
                  <span className="text-slate-500"> · {p.guestName} · {p.destination}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono font-semibold text-slate-700">
                    ₹{Number(p.amountReceived || 0).toLocaleString('en-IN')} received
                  </span>
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => setCancelDialogTripId(p.tripId)}
                    data-testid={`decide-cancellation-btn-${p.tripId}`}
                  >
                    Decide
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        {fromAnalytics && searchTerm ? (
          <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="analytics-filter-banner">
            <div className="text-sm text-emerald-800">
              Showing trips for destination <span className="font-semibold">"{searchTerm}"</span> · from Analytics
            </div>
            <button
              type="button"
              onClick={() => { setSearchTerm(''); navigate('/quotes', { replace: true }); }}
              className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900"
              data-testid="clear-analytics-filter-btn"
            >
              <X className="h-3.5 w-3.5" /> Clear filter
            </button>
          </div>
        ) : null}
        {overdueOnly ? (
          <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200" data-testid="overdue-filter-banner">
            <div className="text-sm text-amber-800">
              Showing <span className="font-semibold">overdue collections</span> · trips with dueDate past + outstanding balance
            </div>
            <button
              type="button"
              onClick={() => navigate('/quotes', { replace: true })}
              className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900"
              data-testid="clear-overdue-filter-btn"
            >
              <X className="h-3.5 w-3.5" /> Clear filter
            </button>
          </div>
        ) : null}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="search-quotes-input"
              placeholder="Search by guest name, email, destination..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter" className="w-full md:w-48">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              {/* Phase 17.2 — Completed is a distinct terminal state from
                  Cancelled. Marking completed triggers an LTV recompute on
                  the backend AND auto-runs reconciliation against the trip's
                  invoices/receipts. */}
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          {/* Phase 12 — quote type filter */}
          <Select value={quoteTypeFilter} onValueChange={setQuoteTypeFilter}>
            <SelectTrigger data-testid="quote-type-filter" className="w-full md:w-48">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="custom">Custom Package</SelectItem>
              <SelectItem value="flight">Flight</SelectItem>
              <SelectItem value="hotel">Hotel</SelectItem>
              <SelectItem value="attraction">Attraction</SelectItem>
              <SelectItem value="visa">Visa</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Trip ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Guest
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Destination
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Pax
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {quotes.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                    No quotes found. Create your first quote to get started!
                  </td>
                </tr>
              ) : (
                quotes.map((quote) => (
                  <tr key={quote.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-teal-700">
                      {quote.displayTripId || quote.tripId || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{quote.guestName}</div>
                        <div className="text-xs text-slate-500">{quote.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {quote.destination}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {quote.adults}A {quote.children > 0 && `${quote.children}C`} {quote.infants > 0 && `${quote.infants}I`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                      ₹{parseFloat(quote.totalCost).toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                        quote.status === 'confirmed'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                          : quote.status === 'completed'
                          ? 'bg-teal-100 text-teal-800 border-teal-200'
                          : quote.status === 'draft'
                          ? 'bg-amber-50 text-amber-700 border-amber-100'
                          : quote.status === 'sent'
                          ? 'bg-blue-50 text-blue-700 border-blue-100'
                          : quote.status === 'cancelled'
                          ? 'bg-rose-50 text-rose-700 border-rose-200'
                          : 'bg-slate-100 text-slate-600 border-slate-200'
                      }`}>
                        {quote.status}
                      </span>
                      {isQuoteExpired(quote) && (
                        <span
                          data-testid={`expired-badge-${quote.id}`}
                          className="ml-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 border border-rose-200"
                        >
                          Expired
                        </span>
                      )}
                      {isQuoteExpired(quote) && canWrite && (
                        <Button
                          data-testid={`renew-btn-${quote.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRenew(quote)}
                          disabled={renewingId === quote.id}
                          className="ml-1 h-6 px-1.5 text-[11px] text-teal-700 hover:bg-teal-50"
                          title="Renew — extends the expiry date"
                        >
                          <RefreshCw className={`h-3 w-3 mr-1 ${renewingId === quote.id ? 'animate-spin' : ''}`} />
                          Renew
                        </Button>
                      )}
                      {needsOptionSelection(quote) && (
                        <span
                          data-testid={`needs-selection-badge-${quote.id}`}
                          title="Multiple pricing options exist but none is selected — pick one before invoicing"
                          className="ml-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                        >
                          Select an option
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-1">
                      {quote.itineraryId ? (
                        <Link to={`/itineraries/${quote.itineraryId}/preview`}>
                          <Button
                            data-testid={`view-btn-${quote.id}`}
                            variant="ghost"
                            size="sm"
                            className="text-sky-600 hover:text-sky-800 hover:bg-sky-50"
                            title="View Itinerary"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                      ) : (
                        <Button
                          data-testid={`view-btn-${quote.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-slate-400"
                          title="No itinerary linked yet"
                          disabled
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      {canWrite && (
                      <Link to={`/quotes/${quote.id}/edit`}>
                        <Button
                          data-testid={`edit-btn-${quote.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-teal-600 hover:text-teal-800"
                          title="Edit Trip"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </Link>
                      )}
                      {showDeleted ? (
                        isAdmin && (
                          <Button
                            data-testid={`restore-btn-${quote.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRestore(quote.id)}
                            className="text-emerald-600 hover:text-emerald-800"
                            title="Restore"
                          >
                            <ArchiveRestore className="h-4 w-4" />
                          </Button>
                        )
                      ) : (
                        <>
                          {isAdmin && (
                            <Button
                              data-testid={`archive-btn-${quote.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSoftDelete(quote.id)}
                              className="text-amber-600 hover:text-amber-800"
                              title="Archive (recoverable)"
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          )}
                          {canWrite && (
                          <Button
                            data-testid={`delete-btn-${quote.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(quote.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete permanently"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CancelTripDialog
        tripId={cancelDialogTripId}
        open={!!cancelDialogTripId}
        onOpenChange={(v) => { if (!v) setCancelDialogTripId(null); }}
        onDone={() => { setCancelDialogTripId(null); fetchPendingCancellations(); fetchQuotes(); }}
      />
    </div>
  );
};

export default QuotesList;
